import cx from 'clsx';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import Button from '@placekit/uikit/components/Button';
import Carousel from '@placekit/uikit/components/Carousel';
import Link from '@placekit/uikit/components/Link';

import FeaturesGrid from 'components/FeaturesGrid';
import FeaturesList from 'components/FeaturesList';
import Section from 'components/Section';
import SnippetsAPI from 'components/SnippetsAPI';
import StoreLocatorAnimation from 'components/StoreLocatorAnimation';
import Subscribe from 'components/Subscribe';
import { useT } from 'i18n';
import { pricingVars } from 'utils/pricing';

// Icons live in the code, copy lives in the dictionaries: each entry pairs a
// dictionary key with the icon to render next to it.
const OVERVIEW = [
  ['dx', 'cube'],
  ['pricing', 'cube-transparent'],
  ['managed', 'server-stack'],
  ['algolia', 'bolt'],
  ['data', 'eye-dropper'],
  ['updates', 'arrow-path'],
  ['lockin', 'lock-open'],
  ['coverage', 'globe-europe-africa'],
  ['languages', 'language'],
  ['privacy', 'shield-check'],
  ['support', 'face-smile'],
];
const AUTOCOMPLETE_ITEMS = [
  ['sota', 'sparkles'],
  ['custom', 'paint-brush'],
  ['device', 'map-pin'],
];
const API_ITEMS = [
  ['rest', 'arrow-path'],
  ['devs', 'code-bracket'],
  ['quality', 'globe-europe-africa'],
  ['patching', 'scissors'],
];
const STORE_LOCATOR_ITEMS = [
  ['unlimited', 'server-stack'],
  ['metadata', 'puzzle-piece'],
  ['map', 'map'],
];

const useItems = (t, namespace, entries, vars) =>
  useMemo(
    () => entries.map(([key, icon]) => ({ icon, ...t(`${namespace}.${key}`, vars) })),
    [entries, namespace, t, vars],
  );

const Features = (props) => {
  const t = useT();
  const vars = useMemo(() => pricingVars(t.locale), [t.locale]);
  const overview = useItems(t, 'home.overview.features', OVERVIEW, vars);
  const autocompleteItems = useItems(t, 'home.features.autocomplete.items', AUTOCOMPLETE_ITEMS);
  const apiItems = useItems(t, 'home.features.api.items', API_ITEMS);
  const storeLocatorItems = useItems(t, 'home.features.storeLocator.items', STORE_LOCATOR_ITEMS);

  return (
    <div className={cx('divide-y divide-gray-200', props.className)}>
      <Section id="overview" theme="gradient-accent">
        <Section.Header title={t('home.overview.title')} center={true}>
          <p className="text-lg text-accent-100">{t('home.overview.subtitle')}</p>
        </Section.Header>
        <FeaturesGrid theme="accent" features={overview} />
        <footer className="flex max-sm:flex-col items-center justify-center gap-y-4 sm:gap-y-0 sm:gap-x-6">
          <Button
            href="https://app.placekit.io/auth/register"
            label={t('common.cta.getStartedFree')}
            kind="secondary"
            size="medium"
          />
          <Button
            href={t.path('/#features')}
            label={t('common.cta.learnMore')}
            kind="none"
            size="medium"
            icon="arrow-down-circle"
            iconPosition="right"
            iconKind="outline"
            className="bg-accent-700 shadow-xs text-white hover:bg-accent-800 focus:bg-accent-800"
          />
        </footer>
      </Section>
      <Section id="features">
        <h2 className="sr-only">{t('home.features.heading')}</h2>
        {/* explicit margin: Tailwind 4 `space-y-12` (Section) skips it after the sr-only heading */}
        <div className="mt-12 space-y-16 lg:space-y-32">
          <article
            id="autocomplete"
            className="flex flex-col md:flex-row justify-between items-center md:items-start gap-12 md:gap-8"
          >
            <FeaturesList
              title={t('home.features.autocomplete.title')}
              description={t('home.features.autocomplete.description')}
              className="shrink-0 md:basis-1/2"
              features={autocompleteItems}
            >
              {/* EN only: the product page has no FR twin. */}
              {t.locale === 'en' && (
                <p className="mt-10 md:pl-16 text-base text-gray-500">
                  Features, code snippets and pricing are detailed on our{' '}
                  <Link
                    href="/address-autocomplete-api"
                    className="text-accent-500 hover:text-accent-600 underline underline-offset-2 decoration-2 decoration-accent-100 hover:decoration-accent-300"
                  >
                    address autocomplete API
                  </Link>{' '}
                  page.
                </p>
              )}
              <footer className="mt-10 flex flex-wrap items-center justify-center md:justify-start md:pl-16 gap-4">
                <Button
                  href="/developers"
                  label={t('home.features.autocomplete.sdks')}
                  kind="neutral"
                  size="medium"
                  icon="cube"
                  iconPosition="right"
                  iconKind="outline"
                />
                <Button
                  href="/developers#examples"
                  scroll={false}
                  label={t('home.features.autocomplete.examples')}
                  kind="neutral"
                  size="medium"
                  icon="code-bracket-square"
                  iconPosition="right"
                  iconKind="outline"
                />
              </footer>
            </FeaturesList>
            <aside className="md:basis-1/2 w-full max-w-lg md:mt-14">
              <Carousel
                autoplay={6000}
                className="aspect-square bg-gray-100 shadow-xl shadow-gray-800/5"
              >
                {[
                  {
                    src: '/media/autocomplete-checkout.png',
                    alt: t('home.features.autocomplete.images.checkout'),
                  },
                  {
                    src: '/media/autocomplete-map.webp',
                    alt: t('home.features.autocomplete.images.map'),
                  },
                  {
                    src: '/media/autocomplete-stores.webp',
                    alt: t('home.features.autocomplete.images.stores'),
                  },
                ].map((img, i) => (
                  <Image
                    key={i.toString()}
                    src={img.src}
                    alt={img.alt}
                    width={1020}
                    height={1020}
                  />
                ))}
              </Carousel>
            </aside>
          </article>
          <article id="api" className="grid md:grid-cols-2 gap-12 md:gap-8 items-start">
            <FeaturesList
              title={t('home.features.api.title')}
              description={t('home.features.api.description')}
              className="min-w-0 md:order-last"
              features={apiItems}
            >
              <footer className="mt-10 flex flex-wrap items-center justify-center md:justify-start md:pl-16 gap-4">
                <Button
                  label={t('common.cta.apiReference')}
                  href="https://api.placekit.io"
                  target="blank"
                  kind="neutral"
                  size="medium"
                  icon="arrow-top-right-on-square"
                  iconPosition="right"
                  iconKind="outline"
                />
                <Button
                  label={t('common.cta.coverage')}
                  href="/terms/coverage"
                  kind="neutral"
                  size="medium"
                  icon="globe-alt"
                  iconPosition="right"
                  iconKind="outline"
                />
              </footer>
            </FeaturesList>
            <aside className="min-w-0 md:mt-14 flex items-start justify-center md:justify-start">
              <SnippetsAPI className="max-w-lg! w-full shadow-xl shadow-gray-800/5" />
            </aside>
          </article>
          <article
            id="store-locator"
            className="flex flex-col md:flex-row justify-between items-center md:items-start gap-12 md:gap-8"
          >
            <FeaturesList
              title={t('home.features.storeLocator.title')}
              description={t('home.features.storeLocator.description')}
              soon={true}
              className="shrink-0 md:basis-1/2"
              features={storeLocatorItems}
            />
            <aside className="md:basis-1/2 w-full max-w-lg">
              <StoreLocatorAnimation />
            </aside>
          </article>
        </div>
        <footer className="text-center">
          <Subscribe list="store-locator" title={t('home.features.subscribe')} className="" />
        </footer>
      </Section>
    </div>
  );
};

Features.propTypes = {
  className: PropTypes.string,
};

export default Features;
